import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import AddIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import type { Theme } from "@mui/material";
import { Button, Divider, Stack, Tooltip, useMediaQuery } from "@mui/material";
import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import adminApi from "../../../api/adminApi";
import useClientContext from "../../../context/useClientContext";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import AssistantChatSession from "./AssistantChatSession";
import { getChatPath, getTaskPath } from "./assistantPaths";
import ChatList from "./ChatList";
import ChatTitle from "./ChatTitle";
import DeleteChatDialog from "./DeleteChatDialog";
import useAssistantChatList from "./useAssistantChatList";

const AssistantPage = () => {
  usePageTitle("Your Assistant");
  const { threadId } = useParams();
  const { clientCode } = useClientContext();
  const navigate = useNavigate();

  const [, createThreadError] = useFetch(adminApi.createAssistantThread, {
    disabled: !!threadId,
    onSetData: (thread) => navigate(getChatPath(clientCode, thread.threadId), { replace: true }),
  });

  const { chatGroups, chatsError, reloadChats, reloadChatsAndMenuBadges, clearChatUnreadDot } = useAssistantChatList();

  const [isHistoryVisible, setIsHistoryVisible] = useState(true);
  const [threadIdToDelete, setThreadIdToDelete] = useState<string>();
  const isSmallerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));

  const handleContentOpen = useCallback(() => {
    if (isSmallerScreen) {
      setIsHistoryVisible(false);
    }
  }, [isSmallerScreen]);

  if (!threadId && createThreadError) {
    return <DataLoadingFailed title="Failed to start the chat" />;
  }

  if (chatsError) {
    logError(chatsError, "[AssistantPage] getAssistantChats");
  }

  const selectedChat = chatGroups?.flatMap((group) => group.chats).find((chat) => chat.threadId === threadId);

  const handleChatSelect = (selectedThreadId: string) => {
    clearChatUnreadDot(selectedThreadId);
    navigate(getChatPath(clientCode, selectedThreadId));
  };

  const handleChatDeleted = (deletedThreadId: string) => {
    setThreadIdToDelete(undefined);
    reloadChatsAndMenuBadges();
    if (deletedThreadId === threadId) {
      navigate(getChatPath(clientCode), { replace: true });
    }
  };

  return (
    <>
      <GeneralPageHeader
        TitleComponent={
          <ChatTitle
            chat={selectedChat}
            isHistoryVisible={isHistoryVisible}
            onToggleHistory={() => setIsHistoryVisible((current) => !current)}
            onRenamed={reloadChats}
          />
        }
      >
        {selectedChat?.taskId && selectedChat.taskName && (
          <Tooltip title={`Task: ${selectedChat.taskName}`}>
            <Button component={Link} to={getTaskPath(clientCode, selectedChat.taskId)}>
              Open task
            </Button>
          </Tooltip>
        )}

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(getChatPath(clientCode))}>
          New Chat
        </Button>

        {selectedChat && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setThreadIdToDelete(selectedChat.threadId)}
          >
            Delete
          </Button>
        )}
      </GeneralPageHeader>

      <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ flex: 1, minHeight: 0 }}>
        {isHistoryVisible && (
          <ChatList
            chatGroups={chatGroups}
            loadingFailed={!!chatsError}
            selectedThreadId={threadId}
            onSelect={handleChatSelect}
            onDelete={setThreadIdToDelete}
          />
        )}

        {threadId ? (
          <AssistantChatSession
            key={threadId}
            threadId={threadId}
            onUpdatesRead={reloadChatsAndMenuBadges}
            onContentOpen={handleContentOpen}
          />
        ) : (
          <InlineLoader />
        )}
      </Stack>

      <DeleteChatDialog
        threadId={threadIdToDelete}
        onCancel={() => setThreadIdToDelete(undefined)}
        onDeleted={handleChatDeleted}
      />
    </>
  );
};

export default AssistantPage;
